import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
//icon
import { BiCalendarCheck } from 'react-icons/bi';
// api
import { getBookStores } from 'api/company';
//helpers
import { verificateNullValue } from 'helpers';
//components
import CustomInput from 'components/backoffice/customInput';
import FooterModal from 'components/backoffice/modals/FooterModal';
import DateTimePicker from 'components/backoffice/dateTimePicker';
import CustomCheckbox from '../customCheckbox';

const PromoCodeForm = ({ onSubmit, promoCode, isEditing, loading }) => {
  const { t } = useTranslation(['backoffice']);
  const navigate = useNavigate();
  const [choices, setChoices] = useState([]);
  const [disabledInput, setDisabledInput] = useState('');

  const {
    formState: { errors },
    setValue,
    control,
    handleSubmit,
  } = useForm();

  const getCompanies = async () => {
    try {
      const { data, status } = await getBookStores();
      if (data && status === 200) {
        const results = data?.results.map((val) => ({ value: val.id, label: val.name }));
        setChoices(results);
      }
    } catch (error) {
      console.log(error);
      setChoices([]);
    }
  };

  useEffect(() => {
    if (isEditing) {
      setValue('title', verificateNullValue(promoCode.title))
      setValue('code', promoCode.code)
      setValue('start', promoCode.start && new Date(promoCode.start))
      setValue('end', promoCode.end && new Date(promoCode.end))
      if (promoCode.discount_percent) setValue('discount_percent', promoCode.discount_percent)
      if (promoCode.discount_amount) setValue('discount_amount', promoCode.discount_amount)
      setValue('max_redemptions', promoCode.max_redemptions)
      setValue('max_redemptions_per_user', promoCode.max_redemptions_per_user)
      setValue('libraries', promoCode.libraries ? promoCode.libraries.map((id) => choices.find((item) => item.value === id)) : [])
      setValue('active', promoCode.active);
      const inputDisabled = promoCode.discount_percent ? 'discount_amount' : 'discount_percent';
      setDisabledInput(inputDisabled)
    }
  }, [promoCode, choices]);

  const submit = (values) => {
    onSubmit(values);
  }

  useEffect(() => {
    getCompanies();
  }, []);

  const handleDisabledInput = (e, name) => {
    const value = e.target.value;
    if (value !== '') {
      setDisabledInput(name)
    } else {
      setDisabledInput('')
    }
  }

  return (
    <div className='mt-5'>
      <Form onSubmit={handleSubmit(submit)}>
        <Row className='mt-4'>
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name='title'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={''}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={t('promoCodes.title')}
                  placeholder={t('promoCodes.title')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors.title && <small className="text-danger ">{errors.title.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name='code'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={''}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={t('promoCodes.code')}
                  placeholder={t('promoCodes.code')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors.code && <small className="text-danger ">{errors.code.message}</small>}
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name='start'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={null}
              render={({ field: { value, onChange } }) => (
                <DateTimePicker
                  label={t('promoCodes.startdate')}
                  className="input-control w-100 form-control"
                  value={value}
                  onChange={onChange}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  showTimeInput={true}
                  timeInputLabel={`${t('promoCodes.timeLabel')}:`}
                  placeholderText={t('promoCodes.placeholderDate')}
                  showIcon={true}
                  icon={<BiCalendarCheck />}
                />
              )}
            />
            {errors.start && <small className="text-danger ">{errors.start.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name='end'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={null}
              render={({ field: { value, onChange } }) => (
                <DateTimePicker
                  label={t('promoCodes.enddate')}
                  className="input-control w-100 form-control"
                  value={value}
                  onChange={onChange}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  showTimeInput={true}
                  timeInputLabel={`${t('promoCodes.timeLabel')}:`}
                  placeholderText={t('promoCodes.placeholderDate')}
                  showIcon={true}
                  icon={<BiCalendarCheck />}
                />
              )}
            />
            {errors.end && <small className="text-danger ">{errors.end.message}</small>}
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name='discount_percent'
              control={control}
              defaultValue={''}
              render={({ field: { onChange, value, name } }) => (
                <CustomInput
                  disabled={disabledInput === name}
                  value={value}
                  type="number"
                  label={t('promoCodes.discountPercent')}
                  placeholder={t('promoCodes.discountPercent')}
                  onChange={(e) => onChange(e)}
                  onInput={(e) => isEditing ? {} : handleDisabledInput(e, 'discount_amount')}
                  min={0}
                />
              )}
            />
            {errors.discount_percent && <small className="text-danger ">{errors.discount_percent.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name='discount_amount'
              control={control}
              defaultValue={''}
              render={({ field: { onChange, value, name } }) => (
                <CustomInput
                  disabled={disabledInput === name}
                  value={value}
                  type="number"
                  label={t('promoCodes.discountAmount')}
                  placeholder={t('promoCodes.discountAmount')}
                  onChange={(e) => onChange(e)}
                  onInput={(e) => isEditing ? {} : handleDisabledInput(e, 'discount_percent')}
                  min={0}
                />
              )}
            />
            {errors.discount_amount && <small className="text-danger ">{errors.discount_amount.message}</small>}
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
            <Controller
              name='max_redemptions'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={''}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="number"
                  label={t('promoCodes.maxRedemptions')}
                  placeholder={t('promoCodes.maxRedemptions')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors.max_redemptions && <small className="text-danger ">{errors.max_redemptions.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name='max_redemptions_per_user'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={''}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="number"
                  label={t('promoCodes.maxRedemptionsUser')}
                  placeholder={t('promoCodes.maxRedemptionsUser')}
                  onChange={(e) => onChange(e)}
                />
              )}
            />
            {errors.max_redemptions_per_user && <small className="text-danger ">{errors.max_redemptions_per_user.message}</small>}
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0 select">
            <Form.Label className="text-light">{t('promoCodes.libraries')}</Form.Label>
            <Controller
              name="libraries"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  noOptionsMessage={() => t('promoCodes.noOptions')}
                  {...field}
                  isMulti
                  options={choices}
                  placeholder={t('promoCodes.placeholderLibraries')}
                  className="select-companies"
                  classNamePrefix='select-companies'
                />
              )}
            />
            {errors.libraries && <small className="text-danger ">{errors.libraries.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={6} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="active"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <CustomCheckbox
                  label={t('activo')}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
          </Col>
        </Row>
      </Form>
      <div className="mt-4 d-flex justify-content-end">
        <div className="m-1 d-flex justify-content-end gap-3">
          <FooterModal
            onCancel={() => navigate(-1)}
            onSave={handleSubmit(submit)}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

PromoCodeForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  promocode: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  loading: PropTypes.bool
};

export default PromoCodeForm;
